import React, { FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  assignOrder,
  getOrderById,
  getOrderUserById,
  putOrder,
  removeAssignOrder,
} from "../../app/services/orderService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";

import Form from "react-bootstrap/Form";
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Col,
  Container,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { defaultOrder, Order } from "../../models/responses/order";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { getOrderTypes } from "../../app/services/orderTypeService";
import { getSuppliers } from "../../app/services/supplierService";
import { getCustomers } from "../../app/services/customerService";
import {
  getOrderStatusById,
  getOrderStatuses,
} from "../../app/services/orderStatusService";
import { OrderStatus } from "../../models/responses/orderStatus";
import { Customer } from "../../models/responses/customer";
import { OrderType } from "../../models/responses/orderType";
import { Supplier } from "../../models/responses/supplier";
import moment from "moment";
import {
  Check,
  CheckAll,
  ExclamationTriangle,
  Pen,
  Plus,
  X,
} from "react-bootstrap-icons";
import { Article, defaultArticle } from "../../models/responses/article";
import {
  setClientToggleLocked,
  setClientType,
} from "../../app/slices/clientTypeSlice";
import { ClientType } from "../../models/enums/clientTypeEnum";
import {
  calculateFromPercentage,
  calculateStoreSalesPriceFromSalesPrice,
  calculateWorkshopPriceFromSalesPrice,
} from "../../app/services/calculationsHelperService";
import { getBrandDiscountByCarBrandId } from "../../app/services/brandDiscountService";
import { getCarBrands } from "../../app/services/carBrandService";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import { getArticleTemplatesByType } from "../../app/services/articleTemplateService";

interface Props { }

const OrderEdit: FC<Props> = () => {
  moment.locale("sv");
  moment.updateLocale(moment.locale(), { invalidDate: "-" });
  const { orderId } = useParams<{ orderId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [displayError, setDisplayError] = useState(false);
  const { orderStatusId } = useParams<{ orderStatusId: string }>();
  const order = useSelector((state: RootState) => state.order.order);
  const orderUser = useSelector((state: RootState) => state.order.orderUser);
  const articleTemplateListTime = useSelector(
    (state: RootState) => state.articleTemplate.articleTemplateListTime,
  );
  const articleTemplateListProduct = useSelector(
    (state: RootState) => state.articleTemplate.articleTemplateListProduct,
  );
  const isLoadingOrderStatuses = useSelector(
    (state: RootState) => state.orderStatus.isWaitingOrderStatusGetResponse,
  );
  const isLoadingOrderTypes = useSelector(
    (state: RootState) => state.orderTypes.isWaitingOrderTypeGetResponse,
  );
  const isLoadingSuppliers = useSelector(
    (state: RootState) => state.supplier.isWaitingSupplierGetResponse,
  );
  const isLoadingCustomers = useSelector(
    (state: RootState) => state.customer.isWaitingCustomerGetResponse,
  );
  const isLoadingCarBrands = useSelector(
    (state: RootState) => state.carBrand.isWaitingCarBrandGetResponse,
  );
  const carBrands = useSelector(
    (state: RootState) => state.carBrand.carBrandList,
  );
  const brandDiscount = useSelector(
    (state: RootState) => state.brandDiscount.brandDiscount,
  );

  const [showAssignedModal, setShow] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [articleErrorMessage, setArticleErrorMessage] = useState<string>("");
  const [orderData, setOrderData] = useState<Order>({ ...defaultOrder });
  const [modalArticle, setModalArticle] = useState<Article>({
    ...defaultArticle,
  });
  const [allProductVerified, setAllProductVerified] = useState(false);
  const [allTimeVerified, setAllTimeVerified] = useState(false);

  useEffect(() => {
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Time));
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Product));
  }, [account, dispatch, instance]);

  useEffect(() => {
    dispatch(getCarBrands(instance, account));
  }, [instance, account, dispatch]);

  useEffect(() => {
    dispatch(setClientToggleLocked(true));
    dispatch(getOrderById(instance, account, orderId));
  }, [account, accounts, dispatch, instance, orderId]);

  useEffect(() => {
    dispatch(assignOrder(instance, account, orderId));
  }, [dispatch, instance, account, orderId]);

  useEffect(() => {
    dispatch(
      getBrandDiscountByCarBrandId(
        instance,
        account,
        orderData?.selectedCarBrandId || "",
      ),
    );
  }, [dispatch, instance, account, orderData?.selectedCarBrandId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      dispatch(getOrderUserById(instance, account, orderId));
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    orderData.userId = orderUser.userId;
  }, [orderUser]);

  useEffect(() => {
    if (orderData.userId && account?.localAccountId) {
      setShow(orderData.userId !== account.localAccountId);
    }
  }, [orderData.userId, account?.localAccountId, orderUser.id]);

  const orderStatus: OrderStatus = useSelector(
    (state: RootState) => state.orderStatus.orderStatus,
  );
  const orderStatusList: OrderStatus[] = useSelector(
    (state: RootState) => state.orderStatus.setOrderStatusList,
  );
  const customers: Customer[] = useSelector(
    (state: RootState) => state.customer.customerList,
  );
  const orderTypes: OrderType[] = useSelector(
    (state: RootState) => state.orderTypes.orderTypeList,
  );
  const suppliers: Supplier[] = useSelector(
    (state: RootState) => state.supplier.supplierList,
  );
  const isWaitingOrderPostResponse = useSelector(
    (state: RootState) => state.order.isWaitingOrderPostResponse,
  );
  const clientType = useSelector(
    (state: RootState) => state.clientType.clientType,
  );

  useEffect(() => {
    if (order && order.id) {
      setOrderData({ ...order });
      if (clientType !== order.clientType) {
        dispatch(setClientType(order.clientType));
      }
    }
  }, [order, clientType, dispatch]);

  useEffect(() => {
    dispatch(getOrderStatusById(instance, account, orderStatusId));
  }, [account, accounts, dispatch, instance, orderStatusId]);

  useEffect(() => {
    dispatch(getOrderStatuses(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getCustomers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getOrderTypes(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getSuppliers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    checkVerified();
  }, [orderData.articles]);

  useEffect(() => {
    setModalArticle({
      ...modalArticle,
      totalExVat: parseFloat(
        (parseFloat(modalArticle.salesPrice) * modalArticle.quantity).toFixed(
          2,
        ),
      ),
    });
  }, [modalArticle.quantity, modalArticle.salesPrice, modalArticle.vat]);

  useEffect(() => {
    orderData.articles?.forEach((article) => {
      articleListSalesPriceHandler(article.rowNumber, article.salesPrice);
      articleSalesPriceInputHandler({
        target: { value: article.salesPrice },
      } as React.ChangeEvent<HTMLInputElement>);
    });
  }, [brandDiscount]);

  const customerInputHandler = (e: string) => {
    setOrderData({ ...orderData, customerId: e });
  };
  const assignedToInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, assignedTo: e.target.value });
  };
  const registrationNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, registrationNumber: e.target.value });
  };
  const vinInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, vin: e.target.value });
  };
  const carInfoInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, carInfo: e.target.value });
  };
  const aoNumberInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, aoNumber: e.target.value });
  };
  const salesOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, salesOrderNumber: e.target.value });
  };
  const purchaseOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, purchaseOrderNumber: e.target.value });
  };
  const orderTypeInputHandler = (e: string) => {
    if (e === "") {
      setOrderData({ ...orderData, orderTypeId: null });
    } else {
      const selectedType = orderTypes.find((type) => type.id === e);
      const newDiscount = selectedType?.discount ?? 0;
      setOrderData({
        ...orderData,
        orderTypeId: e,
        discount: newDiscount,
        articles:
          orderData.articles?.map((article) =>
            article.verified ? article : { ...article, discount: newDiscount },
          ) ?? [],
      });
    }
  };
  const orderedByInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, orderedBy: e.target.value });
  };
  const followUpInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, followUp: e.target.value });
  };
  const emailBodyInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, emailBody: e.target.value });
  };
  const warehouseLocationInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, warehouseLocation: e.target.value });
  };
  const supplierInputHandler = (e: string | null) => {
    if (e === "") e = null;

    if (orderData.articles) {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((article) => {
          if (article.supplierId === null) {
            return { ...article, supplierId: e };
          }
          return article;
        }),
        supplierId: e,
      });
    }
  };
  const arriveDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, arriveDate: e.target.value });
  };
  const bookingDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, bookingDate: e.target.value });
  };
  const deliveryDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, deliveryDate: e.target.value });
  };
  const pickupDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, pickupDate: e.target.value });
  };
  const orderDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, orderDate: e.target.value });
  };
  const filenameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, filename: e.target.value });
  };
  const commentInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, comment: e.target.value });
  };
  const internalCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, internalComment: e.target.value });
  };
  const priceProposalMessageInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, priceProposalMessage: e.target.value });
  };
  const orderApprovedInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, approved: e.target.checked });
  };
  const orderDeniedInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, denied: e.target.checked });
  };
  const orderClientExternalOrderIdHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, clientExternalOrderId: e.target.value });
  };
  const orderStatusInputHandler = (e: string) => {
    setOrderData({ ...orderData, orderStatusId: e });
  };
  const selectedCarBrandInputHandler = (e: string) => {
    if (e === "") {
      setOrderData({ ...orderData, selectedCarBrandId: null });
    } else {
      const selectedBrand = carBrands.find((brand) => brand.id === e);
      setOrderData({
        ...orderData,
        selectedCarBrandId: selectedBrand ? selectedBrand.id : null,
      });
    }
  };

  // ARTICLES // -----------------------------
  const articleListQuantityHandler = (
    rowNumber: number,
    newQuantity: number,
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, quantity: newQuantity }
          : article,
      ),
    });
  };
  const articleListDescriptionInputHandler = (
    rowNumber: number,
    newDescription: string,
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, description: newDescription }
          : article,
      ),
    });
  };
  const articleListArticleNumberHandler = (
    rowNumber: number,
    newArticleNumber: string,
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            articleNumber: newArticleNumber,
            articleTemplateId: null,
          }
          : article,
      ),
    });
  };
  const articleListPurchaseOrderNumberHandler = (
    rowNumber: number,
    newPurchaseOrderNumber: string,
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, purchaseOrderNumber: newPurchaseOrderNumber }
          : article,
      ),
    });
  };
  const articleListCommentHandler = (rowNumber: number, newComment: string) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, comment: newComment }
          : article,
      ),
    });
  };

  const articleListSalesPriceHandler = (
    rowNumber: number,
    newSalesPrice: string,
  ) => {
    let value = newSalesPrice.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (isNaN(parseFloat(value))) value = "0";
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            salesPrice: value,
            storeSalesPrice: calculateStoreSalesPriceFromSalesPrice(
              value,
              brandDiscount,
            ).toString(),
            workshopSalesPrice: calculateWorkshopPriceFromSalesPrice(
              value,
              brandDiscount,
            ).toString(),
          }
          : article,
      ),
    });
  };

  const articleListDiscountHandler = (
    rowNumber: number,
    newDiscount: string,
  ) => {
    if (newDiscount === "") {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((article) =>
          article.rowNumber === rowNumber
            ? { ...article, discount: null }
            : article,
        ),
      });
      return;
    }
    let num = Number(newDiscount);
    if (isNaN(num)) return;
    if (num < 0 || num > 100) return;
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, discount: num }
          : article,
      ),
    });
  };

  const articleListPurchasePriceHandler = (
    rowNumber: number,
    newPurchasePrice: string,
  ) => {
    let value = newPurchasePrice.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (value.includes("%")) {
      value = calculateFromPercentage(
        orderData.articles!.find((article) => article.rowNumber === rowNumber)!
          .salesPrice,
        value,
      ).toString();
    }
    if (isNaN(parseFloat(value))) value = "0";
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            articleTemplateId: null,
            purchasePrice: value,
          }
          : article,
      ),
    });
  };

  const articleListSupplierHandler = (
    rowNumber: number,
    newSupplierId: string | null,
  ) => {
    if (newSupplierId === "") newSupplierId = null;

    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            articleTemplateId: null,
            supplierId: newSupplierId,
          }
          : article,
      ),
    });
  };

  const articleListValidatedHandler = (articleRow: number) => {
    const article = orderData.articles?.find(
      (art) => art.rowNumber === articleRow,
    );
    let errorFields: string[] = [];
    if (!article) {
      errorFields.push("Artikeln hittades inte");
    } else if (!article.verified) {
      if (clientType === ClientType.Fleet && !article.articleNumber) {
        errorFields.push("Artikelnummer saknas");
      }
      if (clientType !== ClientType.Fleet && article.articleType === ArticleType.Product) {
        if (!article.purchasePrice || parseFloat(article.purchasePrice) <= 0) {
          if (clientType === ClientType.Standard && article.quantity > 0) {
            errorFields.push("Inköpspris måste vara mer än 0");
          }
        }
      }
      if (article.articleType === ArticleType.Time && (article.time == null || article.time < 1)) {
        setPendingQuantityRows([articleRow]);
        setShowArticleQuantityErrorModal(true);
        return;
      }
      if (article.articleType !== ArticleType.Time && (article.quantity == null || article.quantity < 1)) {
        setPendingQuantityRows([articleRow]);
        setShowArticleQuantityErrorModal(true);
        return;
      }
    }
    if (errorFields.length > 0) {
      setArticleErrorMessage("Kontrollera: " + errorFields.join(", "));
      setShowArticleErrorModal(true);
      return;
    }

    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((art) =>
        art.rowNumber === articleRow
          ? { ...art, verified: !art.verified }
          : art,
      ),
    });
  };

  const openArticleModalHandler = (article: Article) => {
    setModalArticle({ ...article });
    setShowArticleModal(true);
  };

  const closeArticleModalHandler = () => {
    setShowArticleModal(false);
  };

  const articleArticleNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, articleNumber: e.target.value });
  };
  const articleQuantityInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, quantity: parseInt(e.target.value) });
  };
  const articleDeliveredQuantityInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({
      ...modalArticle,
      deliveredQuantity: parseInt(e.target.value),
    });
  };
  const articleExternalArticleCodeInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, externalArticleCode: e.target.value });
  };
  const articleDiscountInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (value === "") {
      setModalArticle({ ...modalArticle, discount: null });
      return;
    }
    const num = Number(value);
    if (isNaN(num)) return;
    if (num < 0 || num > 100) return;
    setModalArticle({ ...modalArticle, discount: num });
  };
  const articleDescriptionInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, description: e.target.value });
  };
  const articlesalesOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, salesOrderNumber: e.target.value });
  };
  const articlePurchaseOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, purchaseOrderNumber: e.target.value });
  };
  const articleInternalCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, internalComment: e.target.value });
  };
  const articleCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, comment: e.target.value });
  };
  const articlePurchasePriceInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value = e.target.value.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (value.includes("%")) {
      value = calculateFromPercentage(
        modalArticle.salesPrice,
        value,
      ).toString();
    }
    if (isNaN(parseFloat(value))) value = "0";
    setModalArticle({
      ...modalArticle,
      purchasePrice: value,
    });
  };
  const articleSalesPriceInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value = e.target.value.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (isNaN(parseFloat(value))) value = "0";
    setModalArticle({
      ...modalArticle,
      salesPrice: value,
      storeSalesPrice: calculateStoreSalesPriceFromSalesPrice(
        value,
        brandDiscount,
      ).toString(),
      workshopSalesPrice: calculateWorkshopPriceFromSalesPrice(
        value,
        brandDiscount,
      ).toString(),
    });
  };

  const articleSupplierInputHandler = (e: string | null) => {
    if (e === "") e = null;
    setModalArticle({ ...modalArticle, supplierId: e });
  };

  const articleReviewedQuoteInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setModalArticle({ ...modalArticle, reviewedQuote: e.target.checked });
  };

  const articleListTimeHandler = (rowNumber: number, newTime: number) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, time: newTime }
          : article,
      ),
    });
  };

  const saveArticleInModalHandler = () => {
    const savedArticles = orderData.articles?.map<Article>((a) => {
      if (a.rowNumber === modalArticle.rowNumber) return modalArticle;
      return a;
    });

    if (savedArticles) {
      setOrderData({ ...orderData, articles: savedArticles });
    }

    setShowArticleModal(false);
  };

  const validatedHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle({ ...modalArticle, verified: e.target.checked });
  };

  const removeArticleHandler = () => {
    const keptArticles = orderData.articles?.filter((a) => {
      return a.rowNumber !== modalArticle.rowNumber;
    });

    if (keptArticles) {
      setOrderData({ ...orderData, articles: keptArticles });
    }

    setShowArticleModal(false);
  };

  const addNewArticleHandler = () => {
    let newArticle: Article = { ...defaultArticle };
    newArticle.discount = orderData.discount;
    newArticle.rowNumber = calculateRowNumber();
    newArticle.articleType = ArticleType.Product;

    if (orderData.articles === null) return;

    let newList = [...orderData.articles, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const addNewTimeArticleHandler = () => {
    let newArticle: Article = JSON.parse(JSON.stringify(defaultArticle));
    newArticle.rowNumber = calculateRowNumber();
    newArticle.articleType = ArticleType.Time;

    let articlesList = Array.isArray(orderData.articles)
      ? orderData.articles
      : [];
    let newList = [...articlesList, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const calculateRowNumber = () => {
    if (!orderData.articles || orderData.articles.length === 0) return 0;

    return (Math.max(...orderData.articles!.map((a) => a.rowNumber)) ?? 0) + 1;
  };

  const [showArticleErrorModal, setShowArticleErrorModal] = useState(false);
  const [showArticleQuantityErrorModal, setShowArticleQuantityErrorModal] = useState(false);
  const [pendingQuantityRows, setPendingQuantityRows] = useState<number[]>([]);

  const toggleAllArticlesVerified = (articleType: ArticleType) => {
    const newVerifiedValue =
      articleType === ArticleType.Product
        ? !allProductVerified
        : !allTimeVerified;

    let errorFields: string[] = [];
    const lowQuantityRows: number[] = [];

    const updatedArticles =
      orderData.articles?.map((article) => {
        if (article.articleType !== articleType) return article;
        if (newVerifiedValue) {
          if (article.articleType === ArticleType.Time && (article.time == null || article.time < 1)) {
            lowQuantityRows.push(article.rowNumber);
            return article;
          }
          if (article.articleType !== ArticleType.Time && (article.quantity == null || article.quantity < 1)) {
            lowQuantityRows.push(article.rowNumber);
            return article;
          }
          if (clientType === ClientType.Fleet && !article.articleNumber) {
            errorFields.push(
              `Rad ${article.rowNumber}: Artikelnummer saknas (Fleet)`,
            );
            return { ...article, verified: false };
          }
          if (clientType !== ClientType.Fleet && article.articleType === ArticleType.Product) {
            if (
              !article.purchasePrice ||
              parseFloat(article.purchasePrice) <= 0
            ) {
              errorFields.push(
                `Rad ${article.rowNumber}: Inköpspris måste vara mer än 0 (Produkt)`,
              );
              return { ...article, verified: false };
            }
          }
        }
        return { ...article, verified: newVerifiedValue };
      }) ?? [];

    if (errorFields.length > 0) {
      setArticleErrorMessage("Fel: " + errorFields.join(", "));
      setShowArticleErrorModal(true);
    }
    setOrderData({
      ...orderData,
      articles: updatedArticles,
    });

    if (lowQuantityRows.length > 0) {
      setPendingQuantityRows(lowQuantityRows);
      setShowArticleQuantityErrorModal(true);
    }
  };

  const handleConfirmQuantityOverride = () => {
    setShowArticleQuantityErrorModal(false);
    if (pendingQuantityRows.length > 0) {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((art) =>
          pendingQuantityRows.includes(art.rowNumber) ? { ...art, verified: true } : art,
        ),
      });
      setPendingQuantityRows([]);
    }
  };

  const articleTemplateHandler = (
    rowNumber: number,
    templateId: string,
    type: ArticleType,
  ) => {
    const list =
      type === ArticleType.Time
        ? articleTemplateListTime
        : articleTemplateListProduct;

    if (!templateId) {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((article) =>
          article.rowNumber === rowNumber
            ? {
              ...article,
              articleTemplateId: null,
              articleNumber: "",
              supplierId: null,
              purchasePrice: "0",
            }
            : article,
        ),
      });
      return;
    }

    const template = list.find((t) => t.id === templateId);
    if (!template) return;

    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            articleTemplateId: template.id,
            salesPrice: template.keepIncomingPrice
              ? article.salesPrice
              : template.salesPrice,
            articleNumber: template.articleNumber,
            supplierId: null,
            purchasePrice: "0",
          }
          : article,
      ),
    });
  };

  // ARTICLES END // -----------------------------

  const checkVerified = () => {
    const productArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Product,
      ) ?? [];
    const timeArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Time,
      ) ?? [];
    setAllProductVerified(
      productArticles.length > 0 &&
      productArticles.every((art) => art.verified),
    );
    setAllTimeVerified(
      timeArticles.length > 0 && timeArticles.every((art) => art.verified),
    );
  };

  const [showAlreadyExistsModal, setShowAlreadyExistsModal] = useState(false);
  const [pendingOverrideOrder, setPendingOverrideOrder] =
    useState<Order | null>(null);
  const [alreadyExistsMessage, setAlreadyExistsMessage] = useState<string>("");

  const handleUpdateOrder = () => {
    setDisplayError(false);
    dispatch(
      putOrder(
        instance,
        account,
        orderData,
        onSuccess,
        (errorType, errorMessage) => {
          if (errorType === "ALREADY_EXISTS") {
            setAlreadyExistsMessage(
              errorMessage ||
              "Det finns redan en order med detta PostNord-id i systemet.",
            );
            setPendingOverrideOrder({ ...orderData });
            setShowAlreadyExistsModal(true);
          } else {
            setDisplayError(true);
          }
        },
      ),
    );
  };

  const handleOverrideOrder = () => {
    if (pendingOverrideOrder) {
      const overrideOrder = {
        ...pendingOverrideOrder,
        overrideException: true,
      };
      setShowAlreadyExistsModal(false);
      setPendingOverrideOrder(null);
      dispatch(
        putOrder(
          instance,
          account,
          overrideOrder,
          onSuccess,
          (errorType, errorMessage) => {
            setDisplayError(true);
          },
        ),
      );
    }
  };

  const handleCancelOverride = () => {
    setShowAlreadyExistsModal(false);
    setPendingOverrideOrder(null);
  };

  const cancelUpdateOrder = () => {
    dispatch(removeAssignOrder(instance, account, orderId));
    navigateToViewPage();
  };

  const handleAssignOrder = () => {
    dispatch(assignOrder(instance, account, orderId));
    setShow(false);
  };

  const onSuccess = (orderId: string | null) => {
    if (orderId === null) {
      setDisplayError(true);
      return;
    }
    navigateToViewPage();
  };

  const navigateToViewPage = () => {
    setTimeout(() => {
      navigate(
        `/orderStatus/${orderData.orderStatusId}/orders/view/${orderData.id}`,
        { relative: "path" },
      );
    }, 500);
  };

  return (
    <div>
      <Modal
        show={showAlreadyExistsModal}
        onHide={handleCancelOverride}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Varning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {alreadyExistsMessage ||
              "Det finns redan en order med detta PostNord-id i systemet. Vill du skapa den ändå?"}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelOverride}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleOverrideOrder}>
            Spara ändå
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showAssignedModal} onHide={navigateToViewPage}>
        <Modal.Header closeButton>
          <Modal.Title>Låst order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Den här ordern redigeras av {orderData.userName}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={navigateToViewPage}>
            Stäng
          </Button>
          <Button variant="primary" onClick={handleAssignOrder}>
            Lås upp
          </Button>
        </Modal.Footer>
      </Modal>
      <Breadcrumb>
        <Breadcrumb.Item href={`/orderStatus/${orderStatus.id}`}>
          {orderStatus.name}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Uppdatera order</Breadcrumb.Item>
      </Breadcrumb>
      <hr />
      <Form>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Status{" "}
                {isLoadingOrderStatuses && (
                  <div className="ms-3 d-inline-block">
                    <Spinner variant="primary" size="sm" />
                  </div>
                )}
              </Form.Label>
              <Form.Select
                aria-label="Status"
                value={orderData.orderStatusId || ""}
                onChange={(e) => orderStatusInputHandler(e.target.value)}
              >
                {orderStatusList.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            {orderData.exported && (
              <div className="export-badge">
                <Badge bg="success"> Ordern är exporterad till AX</Badge>
              </div>
            )}
          </Col>
          {orderStatus.orderPageType && orderStatus.orderPageType.customer && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Kund{" "}
                  {isLoadingCustomers && (
                    <div className="ms-3 d-inline-block">
                      <Spinner variant="primary" size="sm" />
                    </div>
                  )}
                </Form.Label>
                <Form.Select
                  aria-label="Kund"
                  value={orderData.customerId || ""}
                  onChange={(e) => customerInputHandler(e.target.value)}
                  disabled={orderData.exported}
                >
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} - {customer.externalId}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.assignedTo && (
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ansvarig</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={orderData.assignedTo ?? ""}
                    onInput={assignedToInputHandler}
                  />
                </Form.Group>
              </Col>
            )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.registrationNumber && (
              <Col xs={12} md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Registreringsnummer *</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={orderData.registrationNumber ?? ""}
                    onInput={registrationNumberInputHandler}
                    disabled={orderData.exported}
                  />
                </Form.Group>
              </Col>
            )}
          {clientType === ClientType.Catalog && (
            <Col xs={12} md={2}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Bilmärke{" "}
                  {isLoadingCarBrands && (
                    <div className="ms-3 d-inline-block">
                      <Spinner variant="primary" size="sm" />
                    </div>
                  )}
                </Form.Label>
                <Form.Select
                  aria-label="Bilmärke"
                  value={orderData.selectedCarBrandId || ""}
                  onChange={(e) => selectedCarBrandInputHandler(e.target.value)}
                >
                  <option key={0} value="">
                    - Inget bilmärke valt -
                  </option>
                  {carBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType && orderStatus.orderPageType.vin && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>VIN</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={orderData.vin ?? ""}
                  onInput={vinInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType && orderStatus.orderPageType.carInfo && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Bilmärke</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={orderData.carInfo ?? ""}
                  onInput={carInfoInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {clientType === ClientType.Standard && (
            <>
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.aoNumber && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>AO-nummer *</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={orderData.aoNumber ?? ""}
                        onInput={aoNumberInputHandler}
                        disabled={orderData.exported}
                      />
                    </Form.Group>
                  </Col>
                )}
            </>
          )}
          {orderStatus.orderPageType && orderStatus.orderPageType.foNumber && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>FO-nummer</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={orderData.salesOrderNumber ?? ""}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType && orderStatus.orderPageType.ioNumber && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>IO-nummer</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={orderData.purchaseOrderNumber ?? ""}
                  onInput={purchaseOrderNumberInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType && orderStatus.orderPageType.orderType && (
            <Col xs={12} md={2}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Ordertyp{" "}
                  {isLoadingOrderTypes && (
                    <div className="ms-3 d-inline-block">
                      <Spinner variant="primary" size="sm" />
                    </div>
                  )}
                </Form.Label>
                <Form.Select
                  aria-label="Ordertyp"
                  value={orderData.orderTypeId || ""}
                  onChange={(e) => orderTypeInputHandler(e.target.value)}
                >
                  <option key={0} value="">
                    - Ingen ordertyp vald -
                  </option>
                  {orderTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType &&
            order.clientType === ClientType.Standard && (
              <Col xs={12} md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Rabatt (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={100}
                    value={orderData.discount ?? ""}
                    onChange={(e) => {
                      const value =
                        e.target.value === "" ? null : Number(e.target.value);
                      if (value === null || (value >= 0 && value <= 100)) {
                        setOrderData({
                          ...orderData,
                          discount: value,
                          articles:
                            orderData.articles?.map((article) =>
                              article.verified
                                ? article
                                : { ...article, discount: value },
                            ) ?? [],
                        });
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            )}
          {orderStatus.orderPageType && orderStatus.orderPageType.orderedBy && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Beställd av</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={orderData.orderedBy ?? ""}
                  onInput={orderedByInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType && orderStatus.orderPageType.followUp && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Uppföljning</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={orderData.followUp ?? ""}
                  onInput={followUpInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType && orderStatus.orderPageType.emailBody && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>E-post innehåll</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={orderData.emailBody ?? ""}
                  onInput={emailBodyInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.warehouseLocation && (
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Lagerplats</Form.Label>
                  <Form.Control
                    type="text"
                    defaultValue={orderData.warehouseLocation ?? ""}
                    onInput={warehouseLocationInputHandler}
                  />
                </Form.Group>
              </Col>
            )}
          {(clientType === ClientType.Catalog ||
            (orderStatus.orderPageType &&
              orderStatus.orderPageType.supplier)) && (
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Leverantör{" "}
                    {isLoadingSuppliers && (
                      <div className="ms-3 d-inline-block">
                        <Spinner variant="primary" size="sm" />
                      </div>
                    )}
                  </Form.Label>
                  <Form.Select
                    aria-label="Leverantör"
                    value={orderData.supplierId ?? ""}
                    onChange={(e) => supplierInputHandler(e.target.value)}
                  >
                    <option key={0} value="">
                      - Ingen leverantör vald -
                    </option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.arriveDate && (
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Inkommen</Form.Label>
                  <Form.Control
                    type="date"
                    value={
                      moment(orderData.arriveDate).format("yyyy-MM-DD") ?? ""
                    }
                    onInput={arriveDateInputHandler}
                  />
                </Form.Group>
              </Col>
            )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.bookingDate && (
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Bokad (prod.dag)</Form.Label>
                  <Form.Control
                    type="date"
                    value={
                      moment(orderData.bookingDate).format("yyyy-MM-DD") ?? ""
                    }
                    onInput={bookingDateInputHandler}
                  />
                </Form.Group>
              </Col>
            )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.deliveryDate && (
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Leveransdatum</Form.Label>
                  <Form.Control
                    type="date"
                    value={
                      moment(orderData.deliveryDate).format("yyyy-MM-DD") ?? ""
                    }
                    onInput={deliveryDateInputHandler}
                  />
                </Form.Group>
              </Col>
            )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.pickupTime && (
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Upphämtningsdatum</Form.Label>
                  <Form.Control
                    type="date"
                    value={
                      moment(orderData.pickupDate).format("yyyy-MM-DD") ?? ""
                    }
                    onInput={pickupDateInputHandler}
                  />
                </Form.Group>
              </Col>
            )}
          {orderStatus.orderPageType && orderStatus.orderPageType.orderDate && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Beställt</Form.Label>
                <Form.Control
                  type="date"
                  value={moment(orderData.orderDate).format("yyyy-MM-DD") ?? ""}
                  onInput={orderDateInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {clientType === ClientType.Standard && (
            <>
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.fileName && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Filnamn</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={orderData.filename ?? ""}
                        onInput={filenameInputHandler}
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.carUrl && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>PL URL</Form.Label>
                      <div className="mt-2">
                        <Link
                          target="_blank"
                          className="mt-1"
                          to={orderData.carUrl ?? ""}
                        >
                          {orderData.carUrl ?? ""}
                        </Link>
                      </div>
                    </Form.Group>
                  </Col>
                )}
            </>
          )}
        </Row>
        <Row>
          {orderStatus.orderPageType && orderStatus.orderPageType.comment && (
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Kommentar</Form.Label>
                <Form.Control
                  defaultValue={orderData.comment ?? ""}
                  as="textarea"
                  onInput={commentInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {orderStatus.orderPageType &&
            orderStatus.orderPageType.internalComment && (
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Intern kommentar</Form.Label>
                  <Form.Control
                    as="textarea"
                    defaultValue={orderData.internalComment ?? ""}
                    onInput={internalCommentInputHandler}
                  />
                </Form.Group>
              </Col>
            )}
        </Row>
        {clientType === ClientType.Fleet && (
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meddelande på prisförslag</Form.Label>
                <Form.Control
                  as="textarea"
                  value={orderData.priceProposalMessage ?? ""}
                  onInput={priceProposalMessageInputHandler}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
        {(clientType === ClientType.Catalog ||
          clientType === ClientType.Fleet) && (
            <>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Godkänd av kund"
                      className="mt-2"
                      checked={orderData.approved || false}
                      onChange={orderApprovedInputHandler}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        {clientType === ClientType.Fleet && (
          <>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Nekad av kund"
                    className="mt-2"
                    checked={orderData.denied || false}
                    onChange={orderDeniedInputHandler}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {clientType === ClientType.Fleet && (
          <>
            <Row>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ordernummer hos Postnord</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.clientExternalOrderId ?? ""}
                    onChange={orderClientExternalOrderIdHandler}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Miltal</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.odometer ?? ""}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Hämtad av</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.clientVehicleRetriever ?? ""}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Verkstad</Form.Label>
                  <Form.Control
                    type="text"
                    value={`${orderData.workshopName ?? ""} - ${orderData.workshopId ?? ""}`}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
        <Row>
          <h3>Artiklar</h3>
          <Col xs={12} md={12}>
            <Table responsive striped bordered hover>
              <thead>
                <tr key="-1">
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      toggleAllArticlesVerified(ArticleType.Product)
                    }
                  >
                    {allProductVerified ? (
                      <CheckAll className="text-success" />
                    ) : (
                      <ExclamationTriangle className="text-danger" />
                    )}
                  </th>
                  {clientType === ClientType.Fleet && <th>Välj mall</th>}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTableArticleNumber && (
                      <th>Art.nr</th>
                    )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTableDescription && (
                      <th>Beskrivning</th>
                    )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTableQuantity && (
                      <th>Antal</th>
                    )}
                  {clientType === ClientType.Fleet && (
                    <>
                      <th>Saldo</th>
                    </>
                  )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTableSalesPrice && (
                      <th>Försäljningspris</th>
                    )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTableDiscount &&
                    (clientType === ClientType.Standard ||
                      clientType === ClientType.Fleet) && <th>Rabatt (%)</th>}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTablePurchasePrice && (
                      <th>Inköpspris</th>
                    )}
                  {(clientType === ClientType.Catalog ||
                    (orderStatus.orderPageType &&
                      orderStatus.orderPageType.articleTableSupplierName)) && (
                      <th>Leverantör</th>
                    )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTableIONumber && (
                      <th>IO-nummer</th>
                    )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTableComment && (
                      <th>Extern kommentar</th>
                    )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.articleTablePartUrl &&
                    clientType === ClientType.Standard && <th>PL</th>}

                  {clientType === ClientType.Fleet && (
                    <>
                      <th>Exkluderad från order</th>
                    </>
                  )}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orderData?.articles
                  ?.filter(
                    (article) => article.articleType === ArticleType.Product,
                  )
                  .map((article) => (
                    <tr key={article.rowNumber}>
                      <td>
                        {!article.verified ? (
                          <X
                            className="text-danger"
                            onClick={() => {
                              articleListValidatedHandler(article.rowNumber);
                            }}
                          />
                        ) : (
                          <Check
                            className="text-success"
                            onClick={() => {
                              articleListValidatedHandler(article.rowNumber);
                            }}
                          />
                        )}
                      </td>
                      {clientType === ClientType.Fleet && (
                        <td>
                          <Form.Select
                            size="sm"
                            className="mt-1"
                            value={article.articleTemplateId || ""}
                            disabled={article.verified}
                            onChange={(e) => {
                              if (!e.target.value) {
                                articleTemplateHandler(
                                  article.rowNumber,
                                  "",
                                  ArticleType.Product,
                                );
                                return;
                              }

                              const selectedTemplate =
                                articleTemplateListProduct.find(
                                  (t) => t.id === e.target.value,
                                );
                              if (selectedTemplate) {
                                articleTemplateHandler(
                                  article.rowNumber,
                                  selectedTemplate.id,
                                  ArticleType.Product,
                                );
                              }
                            }}
                          >
                            <option value="">- Välj mall -</option>
                            {articleTemplateListProduct &&
                              articleTemplateListProduct.map((template) => (
                                <option
                                  key={template.id}
                                  value={template.id}
                                >
                                  {template.articleNumber} -{" "}
                                  {template.description}
                                </option>
                              ))}
                          </Form.Select>
                        </td>
                      )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableArticleNumber && (
                          <td>
                            <Form.Control
                              type="text"
                              value={article.articleNumber}
                              size="sm"
                              onChange={(e) =>
                                articleListArticleNumberHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            ></Form.Control>
                          </td>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableDescription && (
                          <td>
                            <Form.Control
                              type="text"
                              value={article.description}
                              size="sm"
                              onChange={(e) =>
                                articleListDescriptionInputHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            ></Form.Control>
                          </td>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableQuantity && (
                          <td>
                            <Form.Control
                              type="number"
                              value={article.quantity}
                              size="sm"
                              onChange={(e) =>
                                articleListQuantityHandler(
                                  article.rowNumber,
                                  parseInt(e.target.value),
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            ></Form.Control>
                          </td>
                        )}
                      {clientType === ClientType.Fleet && (
                        <>
                          <td align="left">{article.stock ?? "-"}</td>
                        </>
                      )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableSalesPrice && (
                          <td>
                            <Form.Control
                              type="text"
                              defaultValue={article.salesPrice ?? 0}
                              value={article.salesPrice}
                              size="sm"
                              onChange={(e) =>
                                articleListSalesPriceHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={
                                article.verified || article.isSyncedWithAX
                              }
                              disabled={
                                article.verified || article.isSyncedWithAX
                              }
                            ></Form.Control>
                          </td>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableDiscount &&
                        (order.clientType === ClientType.Standard ||
                          order.clientType === ClientType.Fleet) && (
                          <td>
                            <Form.Control
                              type="text"
                              defaultValue={article.discount ?? 0}
                              value={article.discount ?? ""}
                              size="sm"
                              onChange={(e) =>
                                articleListDiscountHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            ></Form.Control>
                          </td>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTablePurchasePrice && (
                          <td>
                            <Row>
                              <Col xs={7} className="pe-0">
                                <Form.Control
                                  type="text"
                                  defaultValue={article.purchasePrice ?? 0}
                                  value={article.purchasePrice}
                                  size="sm"
                                  onChange={(e) =>
                                    articleListPurchasePriceHandler(
                                      article.rowNumber,
                                      e.target.value,
                                    )
                                  }
                                  readOnly={
                                    article.verified || !!article.articleTemplateId
                                  }
                                  disabled={
                                    article.verified || !!article.articleTemplateId
                                  }
                                ></Form.Control>
                              </Col>
                              <Col xs={5} className="pt-1">
                                <Form.Text>
                                  {article.supplierId
                                    ? suppliers.find(
                                      (s) => s.id === article.supplierId,
                                    )?.currency
                                    : suppliers.find(
                                      (s) => s.id === orderData.supplierId,
                                    )?.currency}
                                </Form.Text>
                              </Col>
                            </Row>
                          </td>
                        )}
                      {(clientType === ClientType.Catalog ||
                        (orderStatus.orderPageType &&
                          orderStatus.orderPageType
                            .articleTableSupplierName)) && (
                          <td>
                            <Form.Select
                              aria-label="Leverantör"
                              value={article.supplierId || ""}
                              size="sm"
                              onChange={(e) =>
                                articleListSupplierHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              disabled={
                                article.verified || !!article.articleTemplateId
                              }
                            >
                              <option key={0} value="">
                                - Ingen leverantör vald -
                              </option>
                              {suppliers.map((supplier) => (
                                <option key={supplier.id} value={supplier.id}>
                                  {supplier.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableIONumber && (
                          <td>
                            <Form.Control
                              type="text"
                              value={article.purchaseOrderNumber}
                              size="sm"
                              onChange={(e) =>
                                articleListPurchaseOrderNumberHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            ></Form.Control>
                          </td>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableComment && (
                          <td>
                            <Form.Control
                              type="text"
                              value={article.comment}
                              size="sm"
                              onChange={(e) =>
                                articleListCommentHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            ></Form.Control>
                          </td>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTablePartUrl &&
                        clientType === ClientType.Standard && (
                          <td>
                            {!article.partUrl ? (
                              <span>-</span>
                            ) : (
                              <Link
                                onClick={(e) => e.stopPropagation()}
                                target="_blank"
                                to={article.partUrl ?? ""}
                              >
                                <Check className="text-success" />
                              </Link>
                            )}
                          </td>
                        )}
                      {clientType === ClientType.Fleet && (
                        <>
                          <td align="center">
                            {article.excludeFromWarehousePicking ? (
                              <span className="text-danger">Beställ ej</span>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                        </>
                      )}
                      <td
                        onClick={() => openArticleModalHandler(article)}
                        role="button"
                      >
                        <Pen />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
        <Modal
          size="xl"
          show={showArticleModal}
          onHide={() => closeArticleModalHandler()}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {modalArticle.articleNumber
                ? `${modalArticle.articleNumber} - ${modalArticle.description}`
                : "Ny artikel"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col xs={12} md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Beskrivning</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.description ?? ""}
                      onInput={articleDescriptionInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={6} md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Antal beställda</Form.Label>
                    <Form.Control
                      type="number"
                      defaultValue={modalArticle.quantity ?? ""}
                      onInput={articleQuantityInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Antal levererade</Form.Label>
                    <Form.Control
                      type="number"
                      defaultValue={modalArticle.deliveredQuantity ?? ""}
                      onInput={articleDeliveredQuantityInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
                {clientType === ClientType.Fleet && (
                  <>
                    <Col xs={6} md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Lager från AX</Form.Label>
                        <Form.Control
                          type="text"
                          defaultValue={modalArticle.stock ?? "Ej hämtat"}
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Leverantör{" "}
                      {isLoadingSuppliers && (
                        <div className="ms-3 d-inline-block">
                          <Spinner variant="primary" size="sm" />
                        </div>
                      )}
                    </Form.Label>
                    <Form.Select
                      aria-label="Leverantör"
                      value={modalArticle.supplierId || ""}
                      onChange={(e) =>
                        articleSupplierInputHandler(e.target.value)
                      }
                      disabled={
                        modalArticle.verified || !!modalArticle.articleTemplateId
                      }
                    >
                      <option key={0} value="">
                        - Ingen leverantör vald -
                      </option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Artikelnummer</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.articleNumber ?? ""}
                      onInput={articleArticleNumberInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Extern kommentar</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.comment ?? ""}
                      onInput={articleCommentInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Intern kommentar</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.internalComment ?? ""}
                      onInput={articleInternalCommentInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Externt prefix</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.externalArticleCode ?? ""}
                      onInput={articleExternalArticleCodeInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
                {(order.clientType === ClientType.Standard ||
                  order.clientType === ClientType.Fleet) && (
                    <Col xs={12} md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Rabatt (%)</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          value={modalArticle.discount ?? ""}
                          onChange={articleDiscountInputHandler}
                          readOnly={modalArticle.verified}
                          disabled={modalArticle.verified}
                        />
                      </Form.Group>
                    </Col>
                  )}
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>IO-nummer</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.purchaseOrderNumber ?? ""}
                      onInput={articlePurchaseOrderNumberInputHandler}
                      readOnly={modalArticle.verified}
                      disabled={modalArticle.verified}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Artikelnummer i AX</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.adId ?? ""}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Inköpspris</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.purchasePrice ?? 0}
                      value={modalArticle.purchasePrice}
                      onInput={articlePurchasePriceInputHandler}
                      readOnly={
                        modalArticle.verified || !!modalArticle.articleTemplateId
                      }
                      disabled={
                        modalArticle.verified || !!modalArticle.articleTemplateId
                      }
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Försäljningspris</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.salesPrice ?? 0}
                      value={modalArticle.salesPrice}
                      onInput={articleSalesPriceInputHandler}
                      readOnly={
                        modalArticle.verified || modalArticle.isSyncedWithAX
                      }
                      disabled={
                        modalArticle.verified || modalArticle.isSyncedWithAX
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SPN</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.storeSalesPrice ?? 0}
                      value={modalArticle.storeSalesPrice}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>VSN</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.workshopSalesPrice ?? 0}
                      value={modalArticle.workshopSalesPrice}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>PL URL</Form.Label>
                    <div className="mt-2">
                      <Link
                        target="_blank"
                        className="mt-1"
                        to={modalArticle.partUrl ?? ""}
                      >
                        {modalArticle.partUrl ?? ""}
                      </Link>
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Exkluderad från order</Form.Label>
                    <div className="mt-2">
                      <Form.Check
                        type="switch"
                        checked={
                          modalArticle.excludeFromWarehousePicking || false
                        }
                        disabled
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col xs={6} md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Totalt exkl. moms</Form.Label>
                    <Form.Control
                      type="text"
                      value={modalArticle.totalExVat ?? ""}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Container>
              {/* {!orderData.exported && ( */}
              <div className="float-start">
                <Button
                  className="mb-3"
                  variant="secondary"
                  onClick={removeArticleHandler}
                >
                  Ta bort artikel
                </Button>
              </div>
              {/* )} */}

              {/* {!orderData.exported ? ( */}
              <div className="float-end d-inline-flex">
                <div className="me-3">
                  <Form.Check
                    type="switch"
                    label="Granskad offert"
                    className="mt-2"
                    onChange={articleReviewedQuoteInputHandler}
                    checked={modalArticle.reviewedQuote}
                    disabled={orderData.exported}
                    readOnly={orderData.exported}
                  />
                </div>
                <div className="me-3">
                  <Form.Check
                    type="switch"
                    label="Klarmarkerad"
                    className="mt-2"
                    onChange={validatedHandler}
                    checked={modalArticle.verified}
                    disabled={orderData.exported}
                    readOnly={orderData.exported}
                  />
                </div>

                <div>
                  <Button
                    className="ms-3 mb-3"
                    variant="primary"
                    onClick={saveArticleInModalHandler}
                  >
                    Spara ändringar
                  </Button>
                </div>
              </div>
            </Container>
          </Modal.Footer>
        </Modal>
        <Button
          variant="outline-primary"
          className="mb-3"
          onClick={addNewArticleHandler}
        >
          Lägg till artikel <Plus />
        </Button>

        {clientType === ClientType.Fleet && (
          <Col xs={12} md={12} className="mb-3">
            <>
              <h3>Reparationstider</h3>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        toggleAllArticlesVerified(ArticleType.Time)
                      }
                    >
                      {allTimeVerified ? (
                        <CheckAll className="text-success" />
                      ) : (
                        <ExclamationTriangle className="text-danger" />
                      )}
                    </th>
                    <th>Från katalogen</th>
                    <th>Välj mall</th>
                    <th>Art.nummer</th>
                    <th>Beskrivning</th>
                    <th>Antal</th>
                    <th>Försäljningspris</th>
                    <th>Tid</th>
                    <th>Ta bort</th>
                  </tr>
                </thead>
                <tbody>
                  {orderData.articles &&
                    orderData.articles
                      .filter(
                        (article) => article.articleType === ArticleType.Time,
                      )
                      .map((article) => (
                        <tr key={article.rowNumber}>
                          <td>
                            {!article.verified ? (
                              <X
                                className="text-danger"
                                onClick={() => {
                                  articleListValidatedHandler(
                                    article.rowNumber,
                                  );
                                }}
                              />
                            ) : (
                              <Check
                                className="text-success"
                                onClick={() => {
                                  articleListValidatedHandler(
                                    article.rowNumber,
                                  );
                                }}
                              />
                            )}
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={article.externalDescription}
                              size="sm"
                              readOnly
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              className="mt-1"
                              value={article.articleTemplateId || ""}
                              disabled={article.verified}
                              onChange={(e) => {
                                if (!e.target.value) {
                                  articleTemplateHandler(
                                    article.rowNumber,
                                    "",
                                    ArticleType.Time,
                                  );
                                  return;
                                }

                                const selectedTemplate =
                                  articleTemplateListTime.find(
                                    (t) => t.id === e.target.value,
                                  );
                                if (selectedTemplate) {
                                  articleTemplateHandler(
                                    article.rowNumber,
                                    selectedTemplate.id,
                                    ArticleType.Time,
                                  );
                                }
                              }}
                            >
                              <option value="">- Välj mall -</option>
                              {articleTemplateListTime &&
                                articleTemplateListTime.map((template) => (
                                  <option
                                    key={template.id}
                                    value={template.id}
                                  >
                                    {template.articleNumber} -{" "}
                                    {template.description}
                                  </option>
                                ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={article.articleNumber}
                              size="sm"
                              onChange={(e) =>
                                articleListArticleNumberHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={article.description}
                              size="sm"
                              onChange={(e) =>
                                articleListDescriptionInputHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.quantity}
                              size="sm"
                              onChange={(e) =>
                                articleListQuantityHandler(
                                  article.rowNumber,
                                  parseInt(e.target.value),
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.salesPrice}
                              size="sm"
                              onChange={(e) =>
                                articleListSalesPriceHandler(
                                  article.rowNumber,
                                  e.target.value,
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.time}
                              size="sm"
                              onChange={(e) =>
                                articleListTimeHandler(
                                  article.rowNumber,
                                  parseInt(e.target.value),
                                )
                              }
                              readOnly={article.verified}
                              disabled={article.verified}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <X
                              style={{ cursor: orderData.exported ? 'not-allowed' : 'pointer' }}
                              className="text-danger"
                              size={20}
                              onClick={() => {
                                if (orderData.exported) return;
                                const keptArticles = orderData.articles?.filter((a) => a.rowNumber !== article.rowNumber);
                                if (keptArticles) {
                                  setOrderData({ ...orderData, articles: keptArticles });
                                }
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                </tbody>
              </Table>
            </>
          </Col>
        )}
        {clientType === ClientType.Fleet && (
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={addNewTimeArticleHandler}
          >
            Lägg till reparationstid <Plus />
          </Button>
        )}
        <hr />

        <div>
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={cancelUpdateOrder}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            className="mb-3 float-end"
            onClick={handleUpdateOrder}
            disabled={isWaitingOrderPostResponse}
          >
            Uppdatera order
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
        </Alert>
      </Form>
      <Modal
        show={showArticleErrorModal}
        onHide={() => setShowArticleErrorModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Fel vid klarmarkering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {articleErrorMessage ||
            "Du måste ange både inköpspris och försäljningspris (ej 0) innan du kan klarmarkera artikeln."}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowArticleErrorModal(false)}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showArticleQuantityErrorModal}
        onHide={() => {
          setShowArticleQuantityErrorModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Fel vid klarmarkering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Är du säker på att du vill klarmarkera? Angivet antal är 0 på en eller flera artiklar.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowArticleQuantityErrorModal(false);
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmQuantityOverride}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderEdit;
